import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDocumentUpload } from 'Core/hooks';
import CustomPropTypes from 'CustomPropTypes';
import { ZoomableImageModal } from 'Core/components';
import { downloadImage } from 'Core/utils';

const styles = {
  uploadContainer: {
    background: 'white',
    borderRadius: '4px',
    padding: '15px',
  },
  uploadArea: {
    marginBottom: '15px',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '120px',
    border: '2px dashed #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#f9f9f9',
    transition: 'background-color 0.2s',
  },
  uploadContent: {
    textAlign: 'center',
  },
  uploadIcon: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '8px',
  },
  uploadText: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '4px',
  },
  uploadBold: {
    fontWeight: 600,
  },
  uploadSubtext: {
    fontSize: '12px',
    color: '#888',
  },
  hiddenInput: {
    display: 'none',
  },
  previewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '15px',
    marginTop: '15px',
  },
  previewItem: {
    position: 'relative',
    borderRadius: '4px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  previewImageContainer: {
    position: 'relative',
    paddingTop: '75%' /* 4:3 Aspect Ratio */,
  },
  previewImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#ff4444',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  fileName: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '6px 8px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const initData = {
  id: 0,
  inspectionPlanId:0,
  fileName: '',
  filePath: '',
  relativePath: '',
 
};

const SystemRequirement = ({ data, onChanges }) => {

  const [safetyFileInputs, setSafetyFileInputs] = useState(initData);
  const [safetyFiles, setSafetyFiles] = useState(data || []);

  const updateSafetyFileField = (field, value) => {
    setSafetyFileInputs(prevSafetyFileInputs => ({
      ...prevSafetyFileInputs,
      [field]: value,
    }));
  };
  const { uploadDocuments, loading } = useDocumentUpload();
  
  // State for zoomable image modal
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  

  const handleFileUpload = async event => {
    const files = Array.from(event.target.files);

    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('UploadCategory', 'Inspection-SystemRequirement');

    const result = await uploadDocuments(formData);
    if (result) {
      const newFile = {
        id: Math.floor(Math.random() * 101),
        fileName: result.fileName,
        filePath: result.filePath,
        relativePath: result.relativePath,
      };
      const newFiles = [...safetyFiles, newFile];
 
      setSafetyFiles(newFiles);
      setSafetyFileInputs(initData);
      if (onChanges) onChanges(newFiles);
    }
  };

  const removeFile = fileId => {
    const newFiles = safetyFiles.filter(safetyFile => safetyFile.id !== fileId);
    setSafetyFiles(newFiles);
    if (onChanges) onChanges(newFiles);    
  };

  // Handler for opening zoom modal
  const handleImageClick = (e, file) => {
    e.stopPropagation();
    setSelectedImage(file);
    setShowZoomModal(true);
  };

  // Handler for closing zoom modal
  const handleCloseZoomModal = () => {
    setShowZoomModal(false);
    setSelectedImage(null);
  };

  // Handler for download from zoom modal
  const handleZoomModalDownload = (imageSrc) => {
    downloadImage(imageSrc);
  };

  // Sync data prop with safetyFiles state when data changes
  useEffect(() => {
    if (data && Array.isArray(data)) {
      setSafetyFiles(data);
    }
  }, [data]);

  useEffect(() => {
    if (onChanges) onChanges(safetyFiles);
  }, [safetyFiles, onChanges]);

  return (
    <div style={styles.uploadContainer}>
      <div style={styles.uploadArea}>
        <label
          htmlFor="safety-file-upload"
          style={{
            ...styles.uploadLabel,
            backgroundColor: isHovering ? '#f0f0f0' : '#f9f9f9',
          }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div style={styles.uploadContent}>
            <div style={styles.uploadIcon}>↑</div>
            <p style={styles.uploadText}>
              <span style={styles.uploadBold}>Click to upload</span> or drag and drop
            </p>
            <p style={styles.uploadSubtext}>PNG, JPG, GIF up to 10MB</p>
          </div>
          <input
            id="safety-file-upload"
            type="file"
            style={styles.hiddenInput}
            accept="image/*"
            multiple
            onChange={handleFileUpload}
          />
        </label>
      </div>

      {safetyFiles.length > 0 && (
        <div style={styles.previewGrid}>
          {safetyFiles.map(file => (
            <div
              key={file.id}
              style={styles.previewItem}
              onMouseEnter={e => {
                const removeBtn = e.currentTarget.querySelector('.remove-btn');
                if (removeBtn) removeBtn.style.opacity = '1';
              }}
              onMouseLeave={e => {
                const removeBtn = e.currentTarget.querySelector('.remove-btn');
                if (removeBtn) removeBtn.style.opacity = '0';
              }}
            >
              <div style={styles.previewImageContainer}>
                <img 
                  src={file.relativePath} 
                  alt={file.fileName} 
                  style={{...styles.previewImage, cursor: 'pointer'}}
                  onClick={e => handleImageClick(e, file)}
                />
              </div>
              <button
                className="remove-btn"
                onClick={() => removeFile(file.id)}
                style={styles.removeButton}
                title="Remove file"
              >
                ×
              </button>
              <div style={styles.fileName}>{file.fileName}</div>
            </div>
          ))}
        </div>
      )}
      
      {/* Zoomable Image Modal with Editor */}
      <ZoomableImageModal
        show={showZoomModal}
        onHide={handleCloseZoomModal}
        imageSrc={selectedImage?.relativePath}
        imageAlt={selectedImage?.fileName || 'Safety Equipment Image'}
        onDownload={handleZoomModalDownload}
        enableEditor={true} // Set to false to hide editor for specific users
      />
    </div>
  );
};

SystemRequirement.defaultProps = {
  onChanges: () => {},
  data: [],
};

SystemRequirement.propTypes = {
  onChanges: PropTypes.func,
  data: CustomPropTypes.Files,
};

export default SystemRequirement;
